import { useEffect } from "react";
import { Provider, useDispatch } from "react-redux";
import { LDProvider } from "launchdarkly-react-client-sdk";
import { ThemeProvider } from "@emotion/react";
import store from "../../store";
import theme from "../../themes/theme";
import AuthorizationProvider from "../../common/providers/AuthorizationProvider";
import LaunchDarklyProvider from "../../common/launchDarkly/LaunchDarklyProvider";
import { setAccountSlug } from "../../slices";
import SowHeader from "./SowHeader";
import { projectChromeQueryArgs } from "./projectChromeQuery";
import { useV2GetProjectByIdQuery } from "@generated";
import ChangeOrderBanner from "../../pages/ChangeOrders/ChangeOrderBanner/ChangeOrderBanner";
import { changeOrderChild } from "../../pages/ChangeOrders/utils/changeOrderChild";

/* Seeds the slug slice so RTK Query hooks inside SowHeader have
 an accountSlug even though this component is mounted outside of App.js. */
const SowHeaderInner = (props) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (props.account_slug) {
      dispatch(setAccountSlug(props.account_slug));
    }
  }, [props.account_slug]);

  // Same args as SowHeader's query, so this reads its cache entry.
  const { data: projectData } = useV2GetProjectByIdQuery(
    projectChromeQueryArgs(String(props.project_id))
  );
  const { isChangeOrder, parentChangeOrderId } = changeOrderChild(
    projectData?.data
  );

  return (
    <>
      <SowHeader {...props} />
      <ChangeOrderBanner
        isChangeOrder={isChangeOrder}
        parentChangeOrderId={parentChangeOrderId}
      />
    </>
  );
};

/* The classic (release.react-nav OFF) `sow` layout mounts SowHeader as its
 own ReactDOM root, so it needs the same LaunchDarkly + auth provider stack
 that App.js / ProjectLayout wrap SowHeader in — otherwise useFlags() has no
 LDProvider ancestor in production and returns an empty flag set. */
const SowHeaderWithProvider = (props) => (
  <Provider store={store}>
    <AuthorizationProvider account_slug={props.account_slug}>
      <ThemeProvider theme={theme}>
        <LDProvider
          clientSideID={
            process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID || "dummyclient"
          }
        >
          <LaunchDarklyProvider accountSlug={props.account_slug}>
            <SowHeaderInner {...props} />
          </LaunchDarklyProvider>
        </LDProvider>
      </ThemeProvider>
    </AuthorizationProvider>
  </Provider>
);

export default SowHeaderWithProvider;
